import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_MIMES, USA_COUNTRY_CODE } from '@constants/constants';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { CustomValidator } from '@helpers/custom-validator';
import { environment } from '@env/environment';
import { Observable, ReplaySubject } from 'rxjs';
import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import { DocumentsService } from '@services/documents.service';
import { LCLShipmentModel } from '@data-layer/models/lcl';
import { CompanyModel } from '@data-layer/models/companies';
import { enumIncotermsType } from '@data-layer/enums/lcl/enumIncotermsType';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { TrackByService } from '@app/shared/services';

@Component({
  selector: 'app-lcl-shipment-documents',
  templateUrl: './lcl-shipment-documents.component.html',
  styleUrls: ['./lcl-shipment-documents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclShipmentDocumentsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public shipment: LCLShipmentModel;
  @Input() public company: CompanyModel;
  @Input() public linkedES: EntrySummaryModel;
  @Input() public isCanRemove: boolean;
  @Output() public downloadAllFiles: EventEmitter<number[]> = new EventEmitter<number[]>();

  public attachedDocuments: DocumentModel[] = [];
  public attachedSupportDocuments: DocumentModel[] = [];
  public attachedESDocuments: DocumentModel[] = [];
  public allAttachedDocuments: DocumentModel[] = [];
  public availablePluralDocTypes: enumDocumentType[];
  public availableUserDocTypes: enumDocumentType[];
  public currentClickedType: enumDocumentType;
  public enumDocumentType: typeof enumDocumentType = enumDocumentType;
  public ALLOWED_FILE_MIMES: typeof ALLOWED_FILE_MIMES = ALLOWED_FILE_MIMES;
  public fileControl: UntypedFormControl = new UntypedFormControl(null, [
    CustomValidator.fileExtensions(ALLOWED_FILE_EXTENSIONS),
    CustomValidator.maxFileSize(environment.files.default_file_size_limit),
  ]);
  public isLoading: boolean;
  public trackByFn: (item: any) => string | number | string = TrackByService.trackById;

  @ViewChild('input', { static: true }) public inputRef: ElementRef;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(private documentsService: DocumentsService, private changeDetector: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.availablePluralDocTypes = [enumDocumentType.FREIGHT_USER_INVOICE, enumDocumentType.FREIGHT_USER_PACKING_LIST];
    this.availableUserDocTypes = [...this.availablePluralDocTypes];

    // #8599
    if (this.shipment.voyage?.route?.arrivalPort?.country?.code === USA_COUNTRY_CODE) {
      this.availableUserDocTypes.push(enumDocumentType.FREIGHT_USER_ISF_FORM_10_PLUS_2);
    }

    // #7236
    if (this.shipment.incoTermsType === enumIncotermsType.FCA) {
      this.availablePluralDocTypes = [
        ...this.availablePluralDocTypes,
        enumDocumentType.FREIGHT_USER_EXPORT_LICENSE,
        enumDocumentType.FREIGHT_OTHER,
      ];

      this.availableUserDocTypes = [
        ...this.availableUserDocTypes,
        enumDocumentType.FREIGHT_USER_EXPORT_LICENSE,
        enumDocumentType.FREIGHT_OTHER,
      ];
    } else {
      this.availablePluralDocTypes = [...this.availablePluralDocTypes, enumDocumentType.FREIGHT_OTHER];

      this.availableUserDocTypes = [...this.availableUserDocTypes, enumDocumentType.FREIGHT_OTHER];
    }

    this.getAllDocs();

    this.getESDocs();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.linkedES) {
      this.getESDocs();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public onAddOtherDocument(type: enumDocumentType): void {
    this.currentClickedType = type;
    this.inputRef.nativeElement.click();
  }

  public handleFiles(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files && files.length) {
      this.isLoading = true;
      this.uploadFile(files[0], (file: File) => {
        this.uploadDocument(
          {
            relatedEntityId: this.shipment.id,
            relatedEntityType: enumDocumentRelatedEntityType.FREIGHT_SHIPMENT,
            rootEntityType: enumDocumentRootEntityType.FREIGHT_SHIPMENT,
            rootEntityId: this.shipment.id,
            type: this.currentClickedType,
            fileName: file.name,
          },
          file,
        )
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.isLoading = false)),
          )
          .subscribe(() => this.getAllDocs());
      });
    }
    this.inputRef.nativeElement.value = '';
  }

  public uploadDocument(document: DocumentCreateModel, file: File): Observable<any> {
    return this.documentsService.uploadDocument(document, file, this.company.id);
  }

  public uploadDocumentCall(document: DocumentCreateModel, file: File): void {
    this.uploadDocument(document, file)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getAllDocs());
  }

  public removeDocument(documentId: number): void {
    this.documentsService
      .removeDocument(documentId, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getAllDocs());
  }

  public onDocumentStateUpdate(documentId: number, state: enumDocumentState): void {
    this.attachedDocuments = this.attachedDocuments.map((document: DocumentModel) =>
      document.id === documentId
        ? { ...document, state, ...(state === enumDocumentState.UPLOADED && { uploadedDate: new Date().toISOString() }) }
        : document,
    );
  }

  public onDownloadAllFiles(): void {
    this.downloadAllFiles.emit(this.allAttachedDocuments.filter((item: DocumentModel) => !!item.id).map((item: DocumentModel) => item.id));
  }

  public get isUploadedAnyFiles(): boolean {
    return !!this.allAttachedDocuments.filter((item: DocumentModel) => !!item.id).length;
  }

  private getAllDocs(): void {
    this.loadDocuments(true)
      .pipe(take(1))
      .subscribe((docs: DocumentModel[]) => {
        this.allAttachedDocuments = [...docs];
        this.attachedDocuments = this.filterFiles(docs);
        this.attachedSupportDocuments = this.filterSupportFiles(docs);
        this.changeDetector.markForCheck();
      });
  }

  private getESDocs(): void {
    if (!!this.shipment.entrySummary?.referenceNumber && this.shipment.entrySummary?.isInternalFillingSelected && !!this.linkedES?.id) {
      this.loadESDocuments(true)
        .pipe(take(1))
        .subscribe((docs: DocumentModel[]) => {
          this.attachedESDocuments = this.filterESFiles(docs);
          this.changeDetector.markForCheck();
        });
    }
  }

  private uploadFile(file: File, callBack: (file: File) => void): void {
    this.clearFile();
    this.fileControl.patchValue(file);
    this.fileControl.markAsTouched();
    if (this.fileControl.valid) {
      callBack(file);
    }
    this.inputRef.nativeElement.value = null;
  }

  private clearFile(): void {
    this.fileControl.patchValue(null);
    this.fileControl.markAsUntouched();
  }

  private loadDocuments(isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.documentsService.getLCLDocuments(this.shipment.id, isShowLoader).pipe(takeUntil(this.destroy$));
  }

  private loadESDocuments(isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.documentsService.getEntryDocuments(this.linkedES?.id, isShowLoader).pipe(takeUntil(this.destroy$));
  }

  private filterFiles(documents: DocumentModel[]): DocumentModel[] {
    // add docs of this type to the beginning (create empty if none)
    // sort them via fake-id or created id
    // & create result array
    const docsToAdd: enumDocumentType[] = [enumDocumentType.FREIGHT_USER_INVOICE, enumDocumentType.FREIGHT_USER_PACKING_LIST];
    if (this.shipment.incoTermsType === enumIncotermsType.FCA) {
      docsToAdd.push(enumDocumentType.FREIGHT_USER_EXPORT_LICENSE);
    }
    if (this.shipment.voyage?.route?.arrivalPort?.country?.code === USA_COUNTRY_CODE) {
      docsToAdd.push(enumDocumentType.FREIGHT_USER_ISF_FORM_10_PLUS_2);
    }
    const tempDocs: DocumentModel[] = documents;

    docsToAdd.forEach((documentType: enumDocumentType, index: number) => {
      if (!documents.find((document: DocumentModel) => document.type === documentType)) {
        tempDocs.push({
          relatedEntityId: this.shipment.id,
          type: documentType,
          relatedEntityType: enumDocumentRelatedEntityType.FREIGHT_SHIPMENT,
          rootEntityType: enumDocumentRootEntityType.FREIGHT_SHIPMENT,
          rootEntityId: this.shipment.id,
          url: null,
          createdDate: null,
          id: index,
          fileName: null,
          state: enumDocumentState.CREATED,
        });
      }
    });

    return [
      ...tempDocs.filter((doc: DocumentModel) => doc.type === enumDocumentType.FREIGHT_USER_INVOICE),
      ...tempDocs.filter((doc: DocumentModel) => doc.type === enumDocumentType.FREIGHT_USER_PACKING_LIST),
      ...tempDocs.filter((doc: DocumentModel) => doc.type === enumDocumentType.FREIGHT_USER_ISF_FORM_10_PLUS_2),
      ...(this.shipment.incoTermsType === enumIncotermsType.FCA
        ? tempDocs.filter((doc: DocumentModel) => doc.type === enumDocumentType.FREIGHT_USER_EXPORT_LICENSE)
        : []),
      ...tempDocs.filter((doc: DocumentModel) => doc.type === enumDocumentType.FREIGHT_OTHER),
    ];
  }

  private filterSupportFiles(documents: DocumentModel[]): DocumentModel[] {
    const userDocs: enumDocumentType[] = [
      enumDocumentType.FREIGHT_USER_INVOICE,
      enumDocumentType.FREIGHT_USER_PACKING_LIST,
      enumDocumentType.FREIGHT_USER_ISF_FORM_10_PLUS_2,
      enumDocumentType.FREIGHT_USER_EXPORT_LICENSE,
    ];

    return documents.filter((doc: DocumentModel) => !userDocs.includes(doc.type));
  }

  private filterESFiles(documents: DocumentModel[]): DocumentModel[] {
    const userDocs: enumDocumentType[] = [
      enumDocumentType.ARRIVAL_NOTICE,
      enumDocumentType.CARGO_RELEASE_FORM_3461,
      enumDocumentType.CBP_FORM_7501,
      enumDocumentType.DELIVERY_ORDER,
      enumDocumentType.CARGO_RELEASE_FILE,
    ];

    // return only latest
    const filteredFiles: DocumentModel[] = documents
      .sort((a: DocumentModel, b: DocumentModel) => {
        if (a.createdDate > b.createdDate) {
          return 1;
        }
        if (a.createdDate < b.createdDate) {
          return -1;
        }

        return 0;
      })
      .filter((doc: DocumentModel) => userDocs.includes(doc.type));

    return filteredFiles.reduce((acc: DocumentModel[], curr: DocumentModel) => {
      if (!acc.find((item: DocumentModel) => item.type === curr.type)) {
        acc.push(curr);
      }
      return acc;
    }, []);
  }
}
